import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import api from "@/utils/api";
import InspectionResultsView from "@/components/InspectionResultsView";
import {
  Activity, TrendingUp, Package, AlertTriangle,
  CheckCircle2, XCircle, Clock, RefreshCw, ChevronDown, ChevronUp, Layers, Box, X, Eye,
  Zap, RotateCcw, StopCircle, Coffee, MessageSquare, Volume2, VolumeX, ListChecks, Wrench
} from "lucide-react";

const REFRESH_INTERVAL = 15;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [wipSummary, setWipSummary] = useState(null);
  const [toolStatus, setToolStatus] = useState(null);
  const [idleMounts, setIdleMounts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [showAudit, setShowAudit] = useState(false);
  const [error, setError] = useState("");
  const [showAllMachines, setShowAllMachines] = useState(false);
  const [inspectModal, setInspectModal] = useState(null); // { partNo, partName, machine }
  const [downtimeModal, setDowntimeModal] = useState(null); // downtime event
  const [endRunModal, setEndRunModal] = useState(null);     // machine object
  const [resetting, setResetting] = useState(false);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [toast, setToast] = useState("");

  // Sound alerts — persistent toggle, plays a beep on new OVERDUE machine.
  const [soundOn, setSoundOn] = useState(() => {
    const v = localStorage.getItem("ampl_dashboard_sound");
    return v === null ? true : v === "1";
  });
  const [prevOverdue, setPrevOverdue] = useState(new Set());
  useEffect(() => {
    localStorage.setItem("ampl_dashboard_sound", soundOn ? "1" : "0");
  }, [soundOn]);
  const playBeep = () => {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const notes = [880, 660, 880]; // A5, E5, A5 — quick 3-tone alert
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.value = 0.001;
        gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + i * 0.15 + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.15 + 0.14);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + i * 0.15);
        osc.stop(ctx.currentTime + i * 0.15 + 0.15);
      });
      setTimeout(() => ctx.close && ctx.close(), 1200);
    } catch (_) { /* ignore */ }
  };

  const canReset = user?.role === "Main Admin";
  const canSeeShiftStatus = ["Main Admin", "Supervisor"].includes(user?.role);
  const canEndRun = ["Main Admin", "Supervisor", "Inspector"].includes(user?.role);
  const canRemark = canEndRun;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const [dash, wip, ts, idle] = await Promise.all([
        api.get("/dashboard"),
        api.get("/wip-summary").catch(() => ({ data: null })),
        api.get("/tools/status?threshold=85").catch(() => ({ data: null })),
        api.get("/tool-mount/idle-mounted?days=7").catch(() => ({ data: [] })),
      ]);
      setData(dash.data);
      setWipSummary(wip.data);
      setToolStatus(ts.data);
      setIdleMounts(idle.data || []);
      setCountdown(REFRESH_INTERVAL);
      setError("");
    } catch (err) {
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Whenever dashboard data updates, detect transitions to OVERDUE and beep.
  useEffect(() => {
    if (!data?.machines) return;
    const currOverdue = new Set(
      data.machines
        .filter((m) => m.status === "Running" && m.inspection_status === "OVERDUE")
        .map((m) => m.machine_code)
    );
    // First render — seed baseline without beeping.
    if (prevOverdue.size === 0 && currOverdue.size === 0) return;

    const newlyOverdue = [...currOverdue].filter((mc) => !prevOverdue.has(mc));
    if (newlyOverdue.length > 0 && prevOverdue.size > 0 && soundOn) {
      playBeep();
      showToast(`🔔 Inspection overdue on: ${newlyOverdue.join(", ")}`);
    }
    setPrevOverdue(currOverdue);
  }, [data]);

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, REFRESH_INTERVAL * 1000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  useEffect(() => {
    const timer = setInterval(() => setCountdown((c) => (c > 0 ? c - 1 : REFRESH_INTERVAL)), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchAuditLog = async () => {
    try {
      const res = await api.get("/audit-log");
      setAuditLogs(res.data);
    } catch (err) {
      console.error("Failed to load audit log", err);
    }
  };

  const handleAuditToggle = () => {
    if (!showAudit && auditLogs.length === 0) fetchAuditLog();
    setShowAudit((v) => !v);
  };

  useEffect(() => {
    api.get("/downtime/reason-codes").then((r) => setReasonCodes(r.data || [])).catch(() => {});
  }, []);

  const handleShiftReset = async () => {
    if (!window.confirm("Reset stroke counter baselines for the new shift? All OPEN jobs' live_strokes will restart from 0.")) return;
    setResetting(true);
    try {
      const res = await api.post("/shift/reset");
      showToast(`Shift reset: ${res.data?.jobs_reset_count || 0} job(s) reset`);
      fetchDashboard();
    } catch (err) {
      showToast(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const handleSubmitRemark = async (eventId, reasonCode, remark) => {
    try {
      await api.post(`/downtime/${eventId}/remark`, { reason_code: reasonCode, remark });
      showToast("Downtime remark saved");
      setDowntimeModal(null);
      fetchDashboard();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to save remark");
    }
  };

  const handleEndRun = async (jobId, payload) => {
    try {
      const res = await api.post(`/production/${jobId}/end-run`, payload);
      showToast(`Run ended · ${res.data?.strokes || 0} strokes · ${res.data?.ok_qty || 0} OK`);
      setEndRunModal(null);
      fetchDashboard();
    } catch (err) {
      showToast(err.response?.data?.detail || "Failed to end run");
    }
  };

  const kpi = data?.kpis;
  const machines = data?.machines || [];
  // Always show:
  //  - Running machines
  //  - Machines with unmapped setup-strokes (needs attention)
  //  - Machines with pending downtime events (needs remark)
  // Otherwise hide idle machines unless "Show all" is toggled on.
  const needsAttention = (m) =>
    (m.unmapped_alert || (m.setup_strokes || 0) > 0) ||
    (m.downtime_pending_count || 0) > 0;
  const visibleMachines = showAllMachines
    ? machines
    : machines.filter((m) => m.status === "Running" || needsAttention(m));
  const operators = data?.operators || [];
  const rmStatus = data?.rm_status || [];
  const inspection = data?.inspection_summary;
  const missing = data?.missing_data || [];

  const now = new Date();
  const timeStr = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header title="Live Dashboard" />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#1A3560] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-slate-500">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <Header title="Live Dashboard" />

      {/* Dashboard header bar */}
      <div className="bg-[#1A3560] px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6 text-white text-sm">
          <span className="font-bold text-lg">{timeStr}</span>
          <span className="text-white/60">{now.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</span>
          <span className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-xs">Live</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="dashboard-inspection-queue-btn"
            onClick={() => navigate("/inspection-queue")}
            className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all relative"
            title="Open Inspection Queue"
          >
            <ListChecks size={14} />
            Inspection Queue
            {(data?.machines?.filter((m) => m.inspection_status === "OVERDUE").length || 0) > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] font-extrabold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                {data.machines.filter((m) => m.inspection_status === "OVERDUE").length}
              </span>
            )}
          </button>
          <button
            data-testid="dashboard-sound-toggle"
            onClick={() => setSoundOn((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
              soundOn ? "bg-white/10 hover:bg-white/20 text-white" : "bg-red-500/30 hover:bg-red-500/50 text-white"
            }`}
            title={soundOn ? "Sound alerts ON — click to mute" : "Sound alerts MUTED — click to enable"}
          >
            {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
            {soundOn ? "Sound On" : "Muted"}
          </button>
          <button
            data-testid="dashboard-refresh-btn"
            onClick={fetchDashboard}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
          >
            <RefreshCw size={14} className={countdown < 5 ? "animate-spin" : ""} />
            Refresh in {countdown}s
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2 text-red-700 text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-4 py-2 rounded-lg shadow-2xl text-sm font-medium">
          {toast}
        </div>
      )}

      {/* Unmapped-strokes red banner */}
      {(data?.unmapped_machines || []).length > 0 && (
        <div
          data-testid="unmapped-strokes-banner"
          className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 flex items-center gap-3 shadow-md"
        >
          <div className="bg-white/20 p-2 rounded-lg animate-pulse">
            <Zap size={20} />
          </div>
          <div className="flex-1">
            <p className="font-extrabold text-sm">
              ⚠ Unmapped strokes detected on:{" "}
              {data.unmapped_machines.map((mc, i) => (
                <span key={mc}>
                  <button
                    data-testid={`banner-classify-${mc}`}
                    onClick={() => navigate(`/machine/${mc}/classify`)}
                    className="underline hover:text-yellow-200 font-extrabold"
                  >
                    {mc}
                  </button>
                  {i < data.unmapped_machines.length - 1 ? ", " : ""}
                </span>
              ))}
            </p>
            <p className="text-xs text-white/85">
              Click a machine to classify these strokes as setup or start a production run.
            </p>
          </div>
        </div>
      )}

      {/* Shift status bar — info visible to Admin + Supervisor; Reset button is Admin-only */}
      {canSeeShiftStatus && (
        <div className="bg-white border-b border-slate-200 px-6 py-2 flex items-center justify-between text-xs">
          <div className="flex items-center gap-4 text-slate-500">
            <span className="flex items-center gap-1.5">
              <RotateCcw size={13} className="text-slate-400" />
              <span>Shift start: <b className="text-slate-700">05:00 IST</b></span>
            </span>
            {data?.shift?.last_reset_at && (
              <span>
                Last reset:{" "}
                <b className="text-slate-700">{new Date(data.shift.last_reset_at).toLocaleString("en-IN")}</b>
                {" · "}
                by <b className="text-slate-700">{data.shift.last_reset_by}</b>
                {" · "}
                <span className="uppercase text-[10px] px-1.5 py-0.5 bg-slate-100 rounded font-bold">
                  {data.shift.last_method || "?"}
                </span>
              </span>
            )}
            {data?.shift?.in_break && (
              <span className="inline-flex items-center gap-1 text-amber-700 font-bold">
                <Coffee size={13} /> BREAK — downtime alerts paused
              </span>
            )}
          </div>
          {canReset && (
            <button
              data-testid="shift-reset-btn"
              onClick={handleShiftReset}
              disabled={resetting}
              className="inline-flex items-center gap-1.5 bg-[#1A3560] hover:bg-[#152a4d] disabled:bg-slate-300 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-all"
            >
              <RotateCcw size={13} className={resetting ? "animate-spin" : ""} />
              {resetting ? "Resetting..." : "Reset Shift Counters"}
            </button>
          )}
        </div>
      )}

      {/* Downtime pending count strip */}
      {((data?.downtime_pending_total || 0) > 0 || (data?.downtime_pending_historic_total || 0) > 0) && (
        <div
          data-testid="downtime-strip"
          className="bg-red-100 border-b border-red-200 px-6 py-2 text-red-800 text-xs font-bold flex items-center gap-3 flex-wrap"
        >
          {(data?.downtime_pending_total || 0) > 0 && (
            <span className="inline-flex items-center gap-2" data-testid="downtime-today-strip">
              <AlertTriangle size={14} />
              {data.downtime_pending_total} downtime event{data.downtime_pending_total > 1 ? "s" : ""} awaiting remark
              — click the affected machine card below to add a reason.
            </span>
          )}
          {(data?.downtime_pending_historic_total || 0) > 0 && (
            <button
              data-testid="downtime-historic-chip"
              onClick={() => navigate("/admin/downtime-report?pending=1")}
              className="inline-flex items-center gap-1.5 bg-white hover:bg-red-50 text-red-700 border border-red-300 px-2.5 py-1 rounded-full text-[11px] font-extrabold ml-auto"
              title={(data.downtime_pending_historic || []).map((h) => `${h.machine_code}: ${h.count}`).join(", ")}
            >
              <AlertTriangle size={11} />
              {data.downtime_pending_historic_total} historic pending — resolve in Downtime Report
            </button>
          )}
        </div>
      )}

      <div className="p-6 space-y-6 max-w-screen-xl mx-auto">

        {/* Section 1: KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            icon={Activity}
            label="Machines Running"
            sublabel="मशीनें चल रही हैं"
            value={`${kpi?.machines_running || 0} / ${kpi?.total_machines || 0}`}
            color="text-[#1A3560]"
            bg="bg-blue-50"
          />
          <KPICard
            icon={TrendingUp}
            label="Overall Efficiency"
            sublabel="कुल दक्षता"
            value={`${kpi?.overall_efficiency || 0}%`}
            color={kpi?.overall_efficiency >= 85 ? "text-[#15803D]" : kpi?.overall_efficiency >= 70 ? "text-[#D97706]" : "text-[#DC2626]"}
            bg={kpi?.overall_efficiency >= 85 ? "bg-green-50" : kpi?.overall_efficiency >= 70 ? "bg-amber-50" : "bg-red-50"}
          />
          <KPICard
            icon={CheckCircle2}
            label="OK Parts Today"
            sublabel="आज के ठीक पुर्जे"
            value={(kpi?.total_ok_parts || 0).toLocaleString()}
            color="text-[#15803D]"
            bg="bg-green-50"
          />
          <KPICard
            icon={XCircle}
            label="NG Parts Today"
            sublabel="आज के अस्वीकार पुर्जे"
            value={`${(kpi?.total_ng_parts || 0).toLocaleString()} (${kpi?.rejection_pct || 0}%)`}
            color={kpi?.total_ng_parts > 0 ? "text-[#DC2626]" : "text-slate-400"}
            bg={kpi?.total_ng_parts > 0 ? "bg-red-50" : "bg-slate-50"}
          />
        </div>

        {/* Shortfall alert banner */}
        {(wipSummary?.open_shortfalls_today || 0) > 0 && (
          <button
            data-testid="dashboard-shortfall-alert"
            onClick={() => navigate("/admin?tab=shortfalls")}
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-2xl p-4 flex items-center gap-4 shadow-lg active:scale-[0.99] transition-all"
          >
            <div className="bg-white/20 p-3 rounded-xl">
              <AlertTriangle size={28} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-extrabold text-lg leading-tight">
                {wipSummary.open_shortfalls_today} WIP shortfall{wipSummary.open_shortfalls_today > 1 ? "s" : ""} this shift — review
              </p>
              <p className="text-white/80 text-sm">
                Op &gt; 1 jobs ran without sufficient WIP. Tap to reconcile.
              </p>
            </div>
            <span className="text-2xl font-extrabold">→</span>
          </button>
        )}

        {/* Tool Life / PM alerts banner */}
        {((toolStatus?.near_eol?.length || 0) > 0 || (toolStatus?.over_pm?.length || 0) > 0) && (
          <button
            data-testid="dashboard-tool-life-alert"
            onClick={() => navigate("/admin/tool-history")}
            className="w-full bg-gradient-to-r from-red-500 to-orange-600 hover:from-red-600 hover:to-orange-700 text-white rounded-2xl p-4 flex items-center gap-4 shadow-lg active:scale-[0.99] transition-all"
          >
            <div className="bg-white/20 p-3 rounded-xl">
              <Wrench size={28} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-extrabold text-lg leading-tight">
                {(toolStatus?.near_eol?.length || 0) > 0 && (
                  <span>
                    {toolStatus.near_eol.length} tool{toolStatus.near_eol.length > 1 ? "s" : ""} &gt;85% life used
                    {(toolStatus?.over_pm?.length || 0) > 0 && " · "}
                  </span>
                )}
                {(toolStatus?.over_pm?.length || 0) > 0 && (
                  <span>{toolStatus.over_pm.length} tool{toolStatus.over_pm.length > 1 ? "s" : ""} PM due</span>
                )}
              </p>
              <p className="text-white/85 text-sm truncate">
                {(toolStatus.near_eol || []).slice(0, 3).map((t) => `T${t.tool_no} (${t.life_pct}%)`).join(", ")}
                {(toolStatus.over_pm || []).slice(0, 3).map((t) => ` · PM T${t.tool_no} (${t.pm_due_pct}%)`).join("")}
              </p>
            </div>
            <span className="text-2xl font-extrabold">→</span>
          </button>
        )}

        {/* Approaching-PM amber banner (80-99% into PM window) */}
        {(toolStatus?.approaching_pm?.length || 0) > 0 && (
          <button
            data-testid="dashboard-pm-approaching-alert"
            onClick={() => navigate("/admin/tool-history")}
            className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-2xl p-4 flex items-center gap-4 shadow-md active:scale-[0.99] transition-all"
          >
            <div className="bg-white/20 p-3 rounded-xl">
              <Clock size={28} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-extrabold text-lg leading-tight">
                {toolStatus.approaching_pm.length} tool{toolStatus.approaching_pm.length > 1 ? "s" : ""} approaching PM (80-99%)
              </p>
              <p className="text-white/90 text-sm truncate">
                {toolStatus.approaching_pm.slice(0, 4).map((t) => `T${t.tool_no} (${t.pm_due_pct}%)`).join(", ")}
                {" — schedule PM in advance"}
              </p>
            </div>
            <span className="text-2xl font-extrabold">→</span>
          </button>
        )}

        {/* Idle-mounted tools banner (>7 days on machine w/o production) */}
        {idleMounts.length > 0 && (
          <div
            data-testid="dashboard-idle-mounts-banner"
            className="w-full bg-gradient-to-r from-slate-700 to-slate-800 text-white rounded-2xl p-4 flex items-center gap-4 shadow-md"
          >
            <div className="bg-white/20 p-3 rounded-xl">
              <Wrench size={24} />
            </div>
            <div className="flex-1 text-left">
              <p className="font-extrabold text-lg leading-tight">
                {idleMounts.length} tool{idleMounts.length > 1 ? "s" : ""} idle-mounted for &gt; 7 days
              </p>
              <p className="text-white/85 text-sm truncate">
                {idleMounts.slice(0, 4).map((r) => `T${r.tool_no} on ${r.machine_code} (${Math.round(r.days_mounted)}d)`).join(", ")}
                {" — remove or restart production"}
              </p>
            </div>
            <button
              data-testid="dashboard-idle-mounts-open"
              onClick={() => navigate(`/admin/machine-deep-dive/${idleMounts[0].machine_code}`)}
              className="bg-white/20 hover:bg-white/30 rounded-lg px-3 py-2 text-xs font-extrabold"
            >
              Review →
            </button>
          </div>
        )}

        {/* WIP + FG row */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <KPICard
            icon={Wrench}
            label="Tools > 85% Life"
            sublabel="टूल जीवन शेष"
            value={(toolStatus?.near_eol?.length || 0) + ((toolStatus?.over_pm?.length || 0) > 0 ? ` · ${toolStatus.over_pm.length} PM` : "")}
            color={(toolStatus?.near_eol?.length || 0) > 0 ? "text-red-700" : "text-slate-400"}
            bg={(toolStatus?.near_eol?.length || 0) > 0 ? "bg-red-50" : "bg-slate-50"}
            onClick={() => navigate("/admin/tool-history")}
          />
          <KPICard
            icon={Layers}
            label="WIP — Work in Process"
            sublabel="मध्यवर्ती स्टॉक"
            value={(wipSummary?.wip_qty || 0).toLocaleString()}
            color="text-amber-700"
            bg="bg-amber-50"
          />
          <KPICard
            icon={Package}
            label="WIP Lines"
            sublabel="part × op rows"
            value={wipSummary?.wip_lines || 0}
            color="text-amber-700"
            bg="bg-amber-50"
          />
          <KPICard
            icon={Box}
            label="FG — Finished Goods"
            sublabel="तैयार माल"
            value={(wipSummary?.fg_qty || 0).toLocaleString()}
            color="text-emerald-700"
            bg="bg-emerald-50"
          />
          <KPICard
            icon={CheckCircle2}
            label="FG Lines"
            sublabel="parts in FG"
            value={wipSummary?.fg_lines || 0}
            color="text-emerald-700"
            bg="bg-emerald-50"
          />
        </div>

        {/* Section 2: Machine Status Grid */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <SectionTitle title="Machine Status" subtitle="मशीन स्थिति" />
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500" data-testid="machine-count-summary">
                Showing <b>{visibleMachines.length}</b> of {machines.length} machines
                {!showAllMachines && machines.length > visibleMachines.length && (
                  <span className="text-slate-400"> · {machines.length - visibleMachines.length} idle/offline hidden</span>
                )}
              </span>
              <label className="flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="show-all-machines-toggle"
                  checked={showAllMachines}
                  onChange={(e) => setShowAllMachines(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-[#1A3560] focus:ring-[#1A3560]"
                />
                Show all machines
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {visibleMachines.length === 0 ? (
              <div className="col-span-full bg-white border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
                <p className="text-sm text-slate-400">
                  {machines.length === 0 ? "No machines configured" : "No machines running right now"}
                </p>
                {!showAllMachines && machines.length > 0 && (
                  <button
                    onClick={() => setShowAllMachines(true)}
                    className="text-xs text-[#1A3560] font-bold mt-2 underline"
                  >
                    Show all {machines.length} machines
                  </button>
                )}
              </div>
            ) : (
              visibleMachines.map((m) => (
                <MachineCard
                  key={m.machine_code}
                  machine={m}
                  onInspect={(part) => setInspectModal({ ...part, machine: m.machine_code })}
                  onEndRun={canEndRun ? () => setEndRunModal(m) : null}
                  onOpenDowntime={canRemark ? (ev) => setDowntimeModal(ev) : null}
                  onClassify={() => navigate(`/machine/${m.machine_code}/classify`)}
                  onDeepDive={() => navigate(`/admin/machine-deep-dive/${m.machine_code}`)}
                />
              ))
            )}
          </div>
        </div>

        {/* Section 3 & 4: Operator + RM side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Operators */}
          <div>
            <SectionTitle title="Operator Status" subtitle="ऑपरेटर स्थिति" />
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Operator</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Machine</th>
                    <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Stage 2</th>
                  </tr>
                </thead>
                <tbody>
                  {operators.length === 0 ? (
                    <tr><td colSpan={3} className="text-center py-6 text-slate-400">No active operators</td></tr>
                  ) : operators.map((op, i) => (
                    <tr key={op.operator_id} className={`border-t border-slate-100 ${i % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                      <td className="px-4 py-3">
                        <p className="font-bold text-slate-900">{op.operator_name}</p>
                        <p className="text-xs text-slate-400">{op.operator_id}</p>
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-700">{op.machine_code}</td>
                      <td className="px-4 py-3 text-center">
                        {op.stage2_submitted
                          ? <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-bold"><CheckCircle2 size={12} /> Done</span>
                          : <span className="inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-bold"><Clock size={12} /> Pending</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* RM Status */}
          <div>
            <SectionTitle title="RM Status" subtitle="कच्चा माल स्थिति" />
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Batch</th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Grade</th>
                    <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-400">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {rmStatus.length === 0 ? (
                    <tr><td colSpan={3} className="text-center py-6 text-slate-400">No RM batches</td></tr>
                  ) : rmStatus.slice(0, 8).map((rm, i) => (
                    <tr key={rm.batch_code} className={`border-t border-slate-100 ${rm.low_stock ? "bg-red-50" : rm.active ? "bg-blue-50" : i % 2 === 0 ? "" : "bg-slate-50/50"}`}>
                      <td className="px-4 py-2">
                        <p className="font-bold text-slate-900 text-xs">{rm.batch_code}</p>
                        {rm.active && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Active</span>}
                      </td>
                      <td className="px-4 py-2 text-xs text-slate-600">{rm.grade} {rm.thickness_mm}mm</td>
                      <td className="px-4 py-2 text-right">
                        <span className={`font-bold text-sm ${rm.low_stock ? "text-[#DC2626]" : "text-slate-700"}`}>
                          {rm.remaining_qty}kg
                        </span>
                        {rm.low_stock && <p className="text-xs text-red-500">Low stock!</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Section 5: Inspection Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <SectionTitle title="Inspection Summary" subtitle="निरीक्षण सारांश" />
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-slate-900">{inspection?.today_count || 0}</p>
                  <p className="text-xs text-slate-400">Total Today</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-[#15803D]">{inspection?.pass_count || 0}</p>
                  <p className="text-xs text-slate-400">PASS</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-[#DC2626]">{inspection?.fail_count || 0}</p>
                  <p className="text-xs text-slate-400">FAIL</p>
                </div>
              </div>
              {(() => {
                const fc = inspection?.frequency_status_counts;
                const inShift = inspection?.in_shift;
                if (!fc) return null;
                return (
                  <div
                    className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 mb-3"
                    data-testid="inspection-frequency-chip"
                  >
                    <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                      Inspection Frequency {inShift ? "" : <span className="text-slate-400 normal-case tracking-normal font-medium">· off-shift</span>}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs font-bold">
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-[#DC2626]" data-testid="freq-overdue-count">
                        {fc.OVERDUE || 0} OVERDUE
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-amber-100 text-[#D97706]" data-testid="freq-due-count">
                        {fc.DUE || 0} DUE
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-[#15803D]" data-testid="freq-ontime-count">
                        {fc["ON-TIME"] || 0} ON-TIME
                      </span>
                    </div>
                  </div>
                );
              })()}
              {(inspection?.recent_fails || []).length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Recent Failures</p>
                  {inspection.recent_fails.map((f, i) => (
                    <div key={`${f.part}-${f.time}-${i}`} className="text-xs bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-1">
                      <span className="font-bold text-red-700">{f.part}</span>
                      <span className="text-slate-400 mx-1">·</span>
                      <span className="text-slate-600">{(f.fail_dims || []).join(", ")}</span>
                      <span className="float-right text-slate-400">{f.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section 6: Missing Data Alert */}
          <div>
            <SectionTitle title="Missing Data Alert" subtitle="लापता डेटा अलर्ट" />
            <div className={`bg-white rounded-2xl border-2 p-4 ${missing.length > 0 ? "border-[#DC2626]" : "border-slate-200"}`}>
              {missing.length === 0 ? (
                <div className="text-center py-4">
                  <CheckCircle2 size={32} className="text-[#15803D] mx-auto mb-2" />
                  <p className="font-bold text-slate-900 text-sm">All jobs up to date</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-extrabold text-[#DC2626]">{missing.length} job{missing.length > 1 ? "s" : ""} overdue</p>
                    <button
                      onClick={() => navigate("/missing-data")}
                      className="text-xs bg-red-100 text-red-700 px-3 py-1.5 rounded-lg font-bold hover:bg-red-200 transition-all"
                    >
                      Fill Missing Data
                    </button>
                  </div>
                  <div className="space-y-2">
                    {missing.slice(0, 5).map((job) => (
                      <div key={job.job_id} className={`flex justify-between items-center p-2 rounded-lg text-sm ${job.hours_elapsed >= 8 ? "bg-red-50" : "bg-amber-50"}`}>
                        <div>
                          <p className="font-bold text-slate-900">{job.job_id}</p>
                          <p className="text-xs text-slate-500">{job.machine_code} | {job.operator_name}</p>
                        </div>
                        <span className={`text-xs font-extrabold px-2 py-1 rounded-lg ${job.hours_elapsed >= 8 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                          {job.hours_elapsed?.toFixed(1)}h
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Section 7: Audit Log (Admin only) */}
        {user?.role === "Main Admin" && (
          <div>
            <button
              data-testid="toggle-audit-log"
              onClick={handleAuditToggle}
              className="flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 mb-3 transition-all"
            >
              {showAudit ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              Audit Log — Last 50 Changes
            </button>
            {showAudit && (
              <div className="bg-white rounded-2xl border border-slate-200 overflow-auto">
                <table className="w-full text-xs min-w-[700px]">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {["Timestamp", "User", "Role", "Action", "Module", "Record ID", "Detail"].map((h) => (
                        <th key={h} className="text-left px-3 py-2 font-bold uppercase tracking-widest text-slate-400">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log, i) => (
                      <tr key={log.log_id || i} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{log.timestamp?.slice(0, 16)}</td>
                        <td className="px-3 py-2 font-medium text-slate-700">{log.user_id}</td>
                        <td className="px-3 py-2 text-slate-500">{log.role}</td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded font-bold ${log.action === "CREATE" ? "bg-green-100 text-green-700" : log.action === "LOGIN" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-slate-500">{log.module}</td>
                        <td className="px-3 py-2 font-mono text-slate-600">{log.record_id}</td>
                        <td className="px-3 py-2 text-slate-400 max-w-48 truncate">{log.new_value?.slice(0, 50)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {inspectModal && (
        <InspectionModal
          partNo={inspectModal.partNo}
          partName={inspectModal.partName}
          machine={inspectModal.machine}
          onClose={() => setInspectModal(null)}
        />
      )}

      {downtimeModal && (
        <DowntimeRemarkModal
          event={downtimeModal}
          reasonCodes={reasonCodes}
          onSubmit={handleSubmitRemark}
          onClose={() => setDowntimeModal(null)}
        />
      )}

      {endRunModal && (
        <EndRunModal
          machine={endRunModal}
          onSubmit={handleEndRun}
          onClose={() => setEndRunModal(null)}
        />
      )}
    </div>
  );
};

const InspectionModal = ({ partNo, partName, machine, onClose }) => (
  <div
    className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-2 sm:p-6"
    onClick={onClose}
    data-testid="inspection-modal"
  >
    <div
      className="bg-slate-50 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[92vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between z-10 rounded-t-2xl">
        <div className="min-w-0">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold">
            {machine} · Inspection Results & SPC
          </p>
          <p className="font-extrabold text-slate-900 truncate">{partName || partNo}</p>
        </div>
        <button
          data-testid="close-inspection-modal"
          onClick={onClose}
          className="bg-slate-100 hover:bg-slate-200 rounded-full p-2 transition-all shrink-0"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-4">
        {partNo ? (
          <InspectionResultsView initialPartNo={partNo} compact />
        ) : (
          <p className="text-sm text-slate-500 text-center py-8">
            No part number recorded for this job — open the full Inspection Results page to browse.
          </p>
        )}
      </div>
    </div>
  </div>
);

const KPICard = ({ icon: Icon, label, sublabel, value, color, bg, onClick }) => (
  <div
    className={`${bg} rounded-2xl border border-white p-4 shadow-sm ${onClick ? "cursor-pointer hover:shadow-md transition-all" : ""}`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm`}>
        <Icon size={20} className={color} />
      </div>
    </div>
    <p className={`text-2xl font-extrabold ${color} tracking-tight`}>{value}</p>
    <p className="text-xs font-bold text-slate-700 mt-0.5">{label}</p>
    <p className="text-xs text-slate-400">{sublabel}</p>
  </div>
);

const MachineCard = ({ machine: m, onInspect, onEndRun, onOpenDowntime, onClassify, onDeepDive }) => {
  const statusColors = {
    Running: "bg-green-100 text-[#15803D] border-green-200",
    Idle: "bg-slate-100 text-slate-500 border-slate-200",
    Maintenance: "bg-red-100 text-[#DC2626] border-red-200",
  };
  const insp = m.inspection_status; // ON-TIME / DUE / OVERDUE / null
  const hasDowntime = (m.downtime_pending_count || 0) > 0;
  const isSetup = !m.job_id && (m.setup_strokes || 0) > 0;
  const pulseCls = m.stroke_fresh ? "animate-[pulse_1s_ease-in-out_infinite] ring-4 ring-emerald-300/70" : "";

  // Yellow / Red halo on the card border for DUE / OVERDUE running machines
  // or red for downtime / unmapped setup.
  const cardBg = hasDowntime
    ? "bg-white border-[#DC2626] ring-2 ring-red-300"
    : isSetup
    ? "bg-amber-50 border-[#D97706] ring-2 ring-amber-300"
    : m.status === "Running"
    ? insp === "OVERDUE"
      ? "bg-white border-[#DC2626] ring-2 ring-red-200"
      : insp === "DUE"
      ? "bg-white border-[#D97706] ring-2 ring-amber-200"
      : "bg-white border-[#15803D]"
    : "bg-white border-slate-200";
  const effColor = m.efficiency >= 85 ? "text-[#15803D]" : m.efficiency >= 70 ? "text-[#D97706]" : "text-[#DC2626]";
  const canInspect = m.status === "Running" && (m.part_name || m.current_tool);

  const inspBadge =
    insp === "OVERDUE"
      ? (m.ever_inspected === false
          ? { cls: "bg-red-100 text-[#DC2626] border border-red-200", label: "FIRST-PART DUE" }
          : { cls: "bg-red-100 text-[#DC2626] border border-red-200", label: "INSP OVERDUE" })
      : insp === "DUE"
      ? { cls: "bg-amber-100 text-[#D97706] border border-amber-200", label: "INSP DUE" }
      : null;

  const minsSince = m.inspection_mins_since_last;
  const minsTxt =
    minsSince == null
      ? null
      : minsSince < 120
      ? `${Math.round(minsSince)} min`
      : `${(minsSince / 60).toFixed(1)} hrs`;

  return (
    <div
      className={`rounded-xl border-2 p-3 transition-all ${cardBg} ${pulseCls}`}
      data-testid={`machine-card-${m.machine_code}`}
    >
      <div className="flex justify-between items-start mb-2">
        <div className="flex items-center gap-1.5">
          <button
            data-testid={`open-deep-dive-${m.machine_code}`}
            onClick={onDeepDive}
            className="font-extrabold text-sm text-slate-900 hover:text-[#1A3560] hover:underline"
            title="Open Machine Deep-Dive"
          >
            {m.machine_code}
          </button>
          {m.stroke_fresh && (
            <span
              data-testid={`stroke-pulse-${m.machine_code}`}
              className="inline-flex items-center gap-0.5 text-[9px] font-extrabold text-emerald-700 bg-emerald-100 border border-emerald-300 px-1 py-0.5 rounded-full"
              title={`Stroke at ${m.last_stroke_at || ""}`}
            >
              <Zap size={9} /> STROKE
            </span>
          )}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full border font-bold ${statusColors[m.status] || statusColors.Idle}`}>
          {m.status}
        </span>
      </div>

      {isSetup && (
        <button
          data-testid={`setup-badge-${m.machine_code}`}
          onClick={onClassify}
          className="mb-2 w-full flex items-center gap-1 text-[10px] font-extrabold text-amber-800 bg-amber-100 hover:bg-amber-200 border border-amber-300 hover:border-amber-500 px-2 py-1 rounded-lg transition-all cursor-pointer"
          title="Click to classify these strokes as setup or start production"
        >
          <AlertTriangle size={11} /> SETUP · {m.setup_strokes} strokes — click to map
        </button>
      )}

      {m.status === "Running" ? (
        <>
          <p className="text-xs text-slate-600 truncate">T{m.current_tool} — {m.part_name}</p>
          <p className="text-xs text-slate-400 mt-0.5 truncate">{m.operator}</p>
          {inspBadge && (
            <div
              className={`mt-2 inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full ${inspBadge.cls}`}
              data-testid={`inspection-badge-${m.machine_code}`}
              title={
                minsTxt
                  ? `Last inspection ${minsTxt} ago · interval ${m.inspection_interval_hours || "?"}h`
                  : "Never inspected"
              }
            >
              <AlertTriangle size={11} /> {inspBadge.label}{minsTxt ? ` · ${minsTxt}` : ""}
            </div>
          )}
          <div className="mt-2 pt-2 border-t border-slate-100 grid grid-cols-3 gap-1">
            <div><p className="text-[10px] text-slate-400">Parts</p><p className="font-bold text-sm text-slate-900">{m.parts_today}</p></div>
            <div><p className="text-[10px] text-slate-400">Strokes</p><p className="font-bold text-sm text-slate-700">{m.live_strokes || 0}</p></div>
            <div><p className="text-[10px] text-slate-400">Eff</p><p className={`font-bold text-sm ${effColor}`}>{m.efficiency}%</p></div>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-slate-400 mt-1">{m.tonnage}T Press</p>
          {m.mounted_tool_no && (
            <span
              data-testid={`mount-badge-${m.machine_code}`}
              className={`mt-2 inline-flex items-center gap-1 text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${(m.idle_mounted_days || 0) >= 7 ? "text-red-700 bg-red-100 border-red-300" : "text-slate-600 bg-slate-100 border-slate-200"}`}
              title={m.mounted_at ? `Tool mounted since ${new Date(m.mounted_at).toLocaleString("en-IN")}` : "Tool currently mounted"}
            >
              <Wrench size={10} /> T{m.mounted_tool_no} mounted{m.idle_mounted_days != null ? ` · ${m.idle_mounted_days}d idle` : ""}
            </span>
          )}
        </>
      )}
      {m.status === "Running" && m.mounted_tool_no && (
        <p className="text-[10px] text-slate-400 mt-1 inline-flex items-center gap-1" title="Tool physically mounted on machine">
          <Wrench size={9} /> T{m.mounted_tool_no} mounted
        </p>
      )}

      {hasDowntime && onOpenDowntime && (
        <button
          data-testid={`downtime-btn-${m.machine_code}`}
          onClick={() => onOpenDowntime((m.downtime_events || [])[0])}
          className="mt-2 w-full inline-flex items-center justify-center gap-1 text-[10px] font-extrabold text-white bg-[#DC2626] hover:bg-red-700 px-2 py-1.5 rounded-lg transition-all"
        >
          <MessageSquare size={11} /> DOWNTIME · Add Remark ({m.downtime_pending_count})
        </button>
      )}

      <div className="mt-2 pt-1 flex items-center justify-between gap-2">
        {m.check_status === "Done"
          ? <span className="text-xs text-[#15803D] flex items-center gap-1"><CheckCircle2 size={12} /> Check Done</span>
          : <span className="text-xs text-[#DC2626] flex items-center gap-1"><AlertTriangle size={12} /> Check Overdue</span>}
        <div className="flex items-center gap-1">
          {canInspect && onInspect && (
            <button
              data-testid={`inspect-machine-${m.machine_code}`}
              onClick={() => onInspect({ partNo: m.part_no, partName: m.part_name, jobId: m.job_id })}
              className="text-[10px] font-bold text-[#1A3560] hover:bg-[#1A3560]/10 px-2 py-1 rounded inline-flex items-center gap-1 transition-colors"
              title="View inspection results & SPC for this part"
            >
              <Eye size={11} /> Inspect
            </button>
          )}
          {m.status === "Running" && onEndRun && (
            <button
              data-testid={`end-run-${m.machine_code}`}
              onClick={onEndRun}
              className="text-[10px] font-bold text-[#DC2626] hover:bg-red-50 px-2 py-1 rounded inline-flex items-center gap-1 transition-colors"
              title="End this production run"
            >
              <StopCircle size={11} /> End Run
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-3">
    <h3 className="font-extrabold text-slate-900">{title}</h3>
    <p className="text-xs text-slate-400">{subtitle}</p>
  </div>
);

// ---------------- Downtime Remark Modal ----------------

const DowntimeRemarkModal = ({ event, reasonCodes, onSubmit, onClose }) => {
  const [reason, setReason] = useState(reasonCodes[0]?.code || "other");
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!remark.trim()) {
      alert("Please add a remark describing what happened.");
      return;
    }
    setSubmitting(true);
    await onSubmit(event.event_id, reason, remark.trim());
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="downtime-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Downtime Event</p>
            <p className="font-extrabold text-lg">{event.machine_code}</p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-full p-1.5 transition-all"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs space-y-1">
            <p><b>Gap started:</b> {event.gap_start_display || event.gap_start?.slice(11, 16)}</p>
            <p><b>Duration:</b> {event.duration_mins} min</p>
            <p><b>Job:</b> {event.job_id} — {event.part_name}</p>
            <p><b>Operator:</b> {event.operator_name}</p>
          </div>
          <label className="block text-xs font-bold text-slate-700">
            Reason
            <select
              data-testid="downtime-reason-select"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2 focus:ring-2 focus:ring-[#1A3560]"
            >
              {reasonCodes.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-xs font-bold text-slate-700">
            Remark <span className="text-red-500">*</span>
            <textarea
              data-testid="downtime-remark-input"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={3}
              placeholder="What happened during this gap?"
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2 focus:ring-2 focus:ring-[#1A3560]"
            />
          </label>
        </div>
        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-sm font-bold text-slate-600 px-3 py-2 hover:bg-slate-100 rounded-lg transition-all"
          >
            Cancel
          </button>
          <button
            data-testid="downtime-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || !remark.trim()}
            className="text-sm font-extrabold text-white bg-[#DC2626] hover:bg-red-700 disabled:bg-slate-300 px-4 py-2 rounded-lg transition-all"
          >
            {submitting ? "Saving..." : "Save Remark"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------- End Production Run Modal ----------------

const EndRunModal = ({ machine, onSubmit, onClose }) => {
  const [strokes, setStrokes] = useState(machine.live_strokes || 0);
  const [ng, setNg] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [reason, setReason] = useState("supervisor_end");
  const [toolRemoved, setToolRemoved] = useState("no"); // "yes" | "no"
  const [removalReason, setRemovalReason] = useState("production_end");
  const [removalRemark, setRemovalRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (strokes < 0 || ng < 0) {
      alert("Strokes and NG must be non-negative.");
      return;
    }
    setSubmitting(true);
    const payload = {
      strokes: parseInt(strokes) || 0,
      ng_qty: parseInt(ng) || 0,
      loss_remarks: remarks,
      end_reason: reason,
      tool_removed: toolRemoved === "yes",
    };
    if (toolRemoved === "yes") {
      payload.removal_reason = removalReason;
      payload.removal_remark = removalRemark;
    }
    await onSubmit(machine.job_id, payload);
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="end-run-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#1A3560] text-white px-5 py-4 rounded-t-2xl flex items-center justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">End Production Run</p>
            <p className="font-extrabold text-lg">{machine.machine_code}</p>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-full p-1.5 transition-all"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1">
            <p><b>Job:</b> {machine.job_id}</p>
            <p><b>Tool:</b> {machine.current_tool} — {machine.part_name}</p>
            <p><b>Operator:</b> {machine.operator}</p>
            <p><b>Live strokes:</b> {machine.live_strokes || 0}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-bold text-slate-700">
              Strokes
              <input
                data-testid="end-run-strokes"
                type="number"
                min={0}
                value={strokes}
                onChange={(e) => setStrokes(e.target.value)}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              />
            </label>
            <label className="block text-xs font-bold text-slate-700">
              NG Qty
              <input
                data-testid="end-run-ng"
                type="number"
                min={0}
                value={ng}
                onChange={(e) => setNg(e.target.value)}
                className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
              />
            </label>
          </div>
          <label className="block text-xs font-bold text-slate-700">
            End Reason
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
            >
              <option value="supervisor_end">Supervisor Ended Run</option>
              <option value="shift_end">Shift End</option>
              <option value="tool_change">Tool Change Required</option>
              <option value="maintenance">Maintenance Required</option>
              <option value="quality_issue">Quality Issue</option>
              <option value="other">Other</option>
            </select>
          </label>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 mb-2">Tool on Machine</p>
            <p className="text-[11px] text-slate-500 mb-2">Is the tool <b>{machine.current_tool ? `T${machine.current_tool}` : ""}</b> being removed from {machine.machine_code}?</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="end-run-tool-removed-yes"
                onClick={() => setToolRemoved("yes")}
                className={`h-9 rounded-lg text-xs font-extrabold transition-all border ${toolRemoved === "yes" ? "bg-[#DC2626] border-red-700 text-white shadow" : "bg-white border-slate-200 text-slate-500 hover:border-red-300"}`}
              >
                Yes — Remove now
              </button>
              <button
                type="button"
                data-testid="end-run-tool-removed-no"
                onClick={() => setToolRemoved("no")}
                className={`h-9 rounded-lg text-xs font-extrabold transition-all border ${toolRemoved === "no" ? "bg-[#1A3560] border-[#1A3560] text-white shadow" : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"}`}
              >
                No — Keep mounted
              </button>
            </div>
            {toolRemoved === "yes" && (
              <div className="mt-3 space-y-2">
                <label className="block text-xs font-bold text-slate-700">
                  Reason for taking tool off
                  <select
                    data-testid="end-run-removal-reason"
                    value={removalReason}
                    onChange={(e) => setRemovalReason(e.target.value)}
                    className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
                  >
                    <option value="production_end">Production End</option>
                    <option value="preventive_maintenance">Preventive Maintenance</option>
                    <option value="breakdown">Breakdown</option>
                  </select>
                </label>
                <label className="block text-xs font-bold text-slate-700">
                  Remark (optional but recommended for PM / Breakdown)
                  <input
                    data-testid="end-run-removal-remark"
                    value={removalRemark}
                    onChange={(e) => setRemovalRemark(e.target.value)}
                    placeholder="Describe cause / action taken"
                    className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
                  />
                </label>
                <p className="text-[10px] text-slate-500 leading-snug">
                  PM / Breakdown removals will also be logged on the Tool History card automatically.
                </p>
              </div>
            )}
            {toolRemoved === "no" && (
              <p className="text-[11px] text-slate-500 mt-2 leading-snug">
                Tool will stay mounted. You can remove it later from the Machine Deep-Dive page.
                No first-part inspection will be triggered when the same tool starts production again.
              </p>
            )}
          </div>
          <label className="block text-xs font-bold text-slate-700">
            Remarks
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
              placeholder="Optional notes about ending this run"
              className="mt-1 w-full border-slate-300 rounded-lg text-sm px-2 py-2"
            />
          </label>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800">
            <b>⚠</b> Ending this run will close the job (Stage-2) with the values above and fire WIP/RM/FG lineage. This cannot be undone.
          </div>
        </div>
        <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-sm font-bold text-slate-600 px-3 py-2 hover:bg-slate-100 rounded-lg transition-all"
          >
            Cancel
          </button>
          <button
            data-testid="end-run-submit"
            onClick={handleSubmit}
            disabled={submitting}
            className="text-sm font-extrabold text-white bg-[#DC2626] hover:bg-red-700 disabled:bg-slate-300 px-4 py-2 rounded-lg transition-all inline-flex items-center gap-1.5"
          >
            <StopCircle size={14} />
            {submitting ? "Ending..." : "End Run"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
